import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Button, Popconfirm, Divider, Spin, Tag, message } from 'antd';
import PageLoader from '../../../../components/PageLoader/PageLoader';
import OrganisationMainSection from '../../../../components/OrganisationMainSection/OrganisationMainSection';
import Card from '../../../../components/Card/Card';
import Modal from '../../../../components/Modal/Modal';
import Table from '../../../../components/Table/Table';
import ModuleEmptyState from '../../../../components/ModuleEmptyState';
import Export from '../../../../components/Export/Export';
import Import from '../../../../components/Import/Import';
import SupplierEdit from './components/SupplierEdit';
import * as actions from './SupplierAction';
import { orgIdSelector, businessNameSelector } from '../../OrganisationSelectors';
import { supplierListSelector, loadingSuppliersSelector } from './SupplierSelectors';
import { MSG_ERROR } from '../../../../constants';
import { getApiErrorMessage } from '../../../../utils/httpError';
import './Supplier.scss';

const INFO_MSG = (
  <>
    <p>
      Being able to contact any or all of your key or &quot;Strategic Suppliers&quot; which includes public utilities is
      extremely important in the event of a crisis.
    </p>
    <p>You have two options to complete this important section.</p>
    <ol>
      <li>
        For an organisation with a relatively small number of Strategic Suppliers it may be easier to simply complete
        the fields below by using the &quot;Add new&quot; button.
      </li>
      <li>For larger organisations we suggest using the &quot;Import&quot; function.</li>
    </ol>
    <p>
      To Import your Strategic Suppliers first click the &quot;Import&quot; button so you can download the required
      Template. Ensure you complete the template without adding or deleting any columns, then save the spreadsheet and
      import it straight into ContinuityCoach by dragging and dropping the spreadsheet into the clearly marked
      &quot;drag and drop&quot; field, or click the field to allow a search of your computer&apos;s files.
    </p>
    <p>
      There are typically some important or &quot;Strategic Suppliers&quot; that you may feel obliged to contact in the
      event of a crisis which means you potentially cannot receive normal orders or you may require deliveries be sent
      to a different address. Keeping your Strategic Suppliers up to date with developments typically means that
      relationships are maintained in the long term and they are more forgiving and understanding should a disruption to
      your supply to them occur.
    </p>
    <p>
      As with all parts of ContinuityCoach, the more time you invest in this planning process the more valuable the plan
      is likely to be in the event of a crisis.
    </p>
  </>
);

const GUIDANCE = (
  <>
    <strong>What to do:</strong> list critical suppliers and utilities with <strong>Add Supplier</strong> or{' '}
    <strong>Import</strong> so you can reach them quickly during an event.
  </>
);

const EMPTY_HEADLINE = 'No strategic suppliers yet';
const EMPTY_DESCRIPTION =
  'Capture the Strategic Suppliers whose continuity matters to your organisation — utilities, logistics and key vendors should be reachable when operations are strained.';
const EMPTY_TIP =
  'Tip: Download the CSV template, add your suppliers, and upload to import multiple records at once.';

const Supplier = ({
  orgId,
  businessName,
  loading,
  supplierList,
  fetchSupplier,
  deleteSupplier,
  downloadSupplierImportTemplate,
  exportSuppliers,
  importSupplier
}) => {
  useEffect(() => {
    fetchSupplier(orgId);
  }, [orgId, fetchSupplier]);

  const onDelete = async supplier => {
    try {
      await deleteSupplier(orgId, supplier.supplierId);
      message.success('The supplier has been successfully deleted');
    } catch (err) {
      message.error(getApiErrorMessage(err, MSG_ERROR));
    }
  };

  const columns = [
    { key: 'name', title: 'Name', dataIndex: 'name', sorter: (a, b) => a.name.localeCompare(b.name) },
    {
      key: 'category',
      title: 'Category',
      dataIndex: 'category',
      render: category => {
        try {
          const parsedCategory = JSON.parse(category);
          return (
            <div>
              {parsedCategory.map(tag => (
                <Tag className="category-name" key={tag}>
                  {tag}
                </Tag>
              ))}
            </div>
          );
        } catch {
          return (
            <Tag className="category-name" key={category}>
              {category}
            </Tag>
          );
        }
      }
    },
    {
      key: 'contactPerson',
      title: 'Contact Person',
      dataIndex: 'contactPerson'
    },
    {
      key: 'email',
      title: 'Email',
      dataIndex: 'email',
      width: 200,
      render: text => (text ? <a href={`mailto:${text}`}>{text}</a> : null)
    },
    {
      key: 'phone',
      title: 'Phone',
      dataIndex: 'phone',
      render: text => (text ? <a href={`tel:${text}`}>{text}</a> : null)
    },
    {
      key: 'action',
      width: 115,
      render: (text, record) => (
        <span>
          <Modal explanation toggle={show => <Button type="link" icon="edit" onClick={show} />}>
            {(hide, setExplanation) => (
              <SupplierEdit supplierInfo={record} close={hide} onExplanationChange={setExplanation} />
            )}
          </Modal>
          <Divider type="vertical" />
          <Popconfirm title="Sure to delete?" onConfirm={() => onDelete(record)}>
            <Button type="link" icon="delete" />
          </Popconfirm>
        </span>
      )
    }
  ];

  const hasSuppliers = supplierList && supplierList.length > 0;

  const emptySlot = (
    <ModuleEmptyState
      moduleTitle="Strategic Suppliers"
      embedded
      headline={EMPTY_HEADLINE}
      description={EMPTY_DESCRIPTION}
      tip={EMPTY_TIP}
      iconType="shopping-cart"
    >
      <Modal
        explanation
        toggle={show => (
          <Button type="primary" className="module-empty-state__btn-primary" htmlType="button" onClick={show}>
            Add First Supplier
          </Button>
        )}
      >
        {(hide, setExplanation) => <SupplierEdit close={hide} onExplanationChange={setExplanation} />}
      </Modal>
      <Modal
        title="Import Suppliers"
        toggle={show => (
          <Button className="module-empty-state__btn-secondary" htmlType="button" onClick={show}>
            Import from CSV
          </Button>
        )}
      >
        {() => (
          <Import
            templateName="suppliers_template.xlsx"
            downloadTemplate={downloadSupplierImportTemplate}
            importFromTemplate={file => importSupplier(orgId, file)}
          />
        )}
      </Modal>
    </ModuleEmptyState>
  );

  const toolbarActions = (
    <>
      <Modal
        title="Import Suppliers"
        toggle={show => (
          <Button
            type="default"
            className="organisation-main-section__toolbar-ghost"
            icon="contacts"
            htmlType="button"
            onClick={show}
          >
            Import
          </Button>
        )}
      >
        {() => (
          <Import
            templateName="suppliers_template.xlsx"
            downloadTemplate={downloadSupplierImportTemplate}
            importFromTemplate={file => importSupplier(orgId, file)}
          />
        )}
      </Modal>
      <Export
        fileName={`${businessName.replace(/\s+/g, '_')}_Suppliers.xlsx`}
        exportFile={() => exportSuppliers(orgId)}
      >
        {onExport => (
          <Button
            type="default"
            className="organisation-main-section__toolbar-ghost"
            icon="export"
            htmlType="button"
            onClick={onExport}
          >
            Export
          </Button>
        )}
      </Export>
      <Modal
        explanation
        toggle={show => (
          <Button
            type="primary"
            className="organisation-main-section__toolbar-primary"
            htmlType="button"
            onClick={show}
          >
            Add Supplier
          </Button>
        )}
      >
        {(hide, setExplanation) => <SupplierEdit close={hide} onExplanationChange={setExplanation} />}
      </Modal>
    </>
  );

  return supplierList ? (
    <OrganisationMainSection
      title="Strategic Suppliers"
      tooltipContent={INFO_MSG}
      guidance={GUIDANCE}
      toolbarActions={toolbarActions}
      flowPrev="/organization/customers"
      flowNext="/organization/strategic-third-parties"
    >
      <Spin spinning={loading}>
        {hasSuppliers ? (
          <Card>
            <Table
              title="Suppliers"
              columns={columns}
              dataSource={supplierList}
              rowKey="supplierId"
              searchFields={['name', 'category', 'contactPerson', 'email', 'phone']}
            />
          </Card>
        ) : (
          emptySlot
        )}
      </Spin>
    </OrganisationMainSection>
  ) : (
    <PageLoader tip="Loading Suppliers..." />
  );
};

const mapStateToProps = state => ({
  orgId: orgIdSelector(state),
  businessName: businessNameSelector(state),
  supplierList: supplierListSelector(state),
  loading: loadingSuppliersSelector(state)
});

const mapDispatchToProps = { ...actions };

export default connect(mapStateToProps, mapDispatchToProps)(Supplier);
